import React, { useState, useContext } from 'react';
import { CircularProgress } from '@mui/material';
import { trackPostHogEvent } from '../../utils/posthogUtils';
import { SourceContext } from '../../utils/sourceContext';
import styles from './AffiliateSignup.module.css';

function AffiliateSignup() {
    const { source } = useContext(SourceContext);
    const [phoneDisplayValue, setPhoneDisplayValue] = useState('');
    const [form, setForm] = useState({
        name: '',
        email: '',
        phone: '',
    });
    const [formErrors, setFormErrors] = useState({
        name: '',
        email: '',
        phone: '',
    });
    const [submitted, setSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');

    // Handler for form field changes
    const handleInputChange = (e) => {
        const { name, value, } = e.target;

        setForm(prevData => ({
            ...prevData,
            [name]: value
        }));

    }

    // Handler for clearing error messages on focus
    const handleClearError = (e) => {
        const { name } = e.target;
        setFormErrors(prevErrors => ({ ...prevErrors, [name]: '' }));
    }

    // Helper function to limit phone input to digits only
    const handlePhoneKeyDown = (e) => {
        // Allow backspace, delete, tab, escape, enter, and arrow keys
        if ([8, 9, 27, 13, 46, 37, 38, 39, 40].includes(e.keyCode) ||
            // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
            (e.keyCode === 65 && e.ctrlKey) ||
            (e.keyCode === 67 && e.ctrlKey) ||
            (e.keyCode === 86 && e.ctrlKey) ||
            (e.keyCode === 88 && e.ctrlKey)) {
            return;
        }
        
        // Only allow digits
        if ((e.keyCode < 48 || e.keyCode > 57) && (e.keyCode < 96 || e.keyCode > 105)) {
            e.preventDefault();
        }
    };

    const formatPhoneDisplay = (value) => {
        if (!value) return '';
        const digits = value.replace(/\D/g, '');
        
        if (digits.length >= 10) {
            return `(${digits.substr(0, 3)}) ${digits.substr(3, 3)}-${digits.substr(6, 4)}`;
        } else if (digits.length >= 6) {
            return `(${digits.substr(0, 3)}) ${digits.substr(3, 3)}-${digits.substr(6)}`;
        } else if (digits.length >= 3) {
            return `(${digits.substr(0, 3)}) ${digits.substr(3)}`;
        } else if (digits.length > 0) {
            return `(${digits}`;
        }
        return '';
    };

    const handlePhoneChange = (e) => {
        const value = e.target.value;
        const digits = value.replace(/\D/g, '');
        const limitedDigits = digits.substr(0, 10);
        const formattedValue = formatPhoneDisplay(limitedDigits);
        
        setPhoneDisplayValue(formattedValue);
        setForm(prevData => ({
            ...prevData,
            phone: limitedDigits
        }));

        localStorage.setItem('checkout_form_data', JSON.stringify({
            ...form,
            phone: limitedDigits
        }));

    };

    // Validation helpers
    const validateName = () => {
        if (!form.name || form.name.trim() === '') {
            setFormErrors(prevErrors => ({ ...prevErrors, name: 'Your name is required.' }));
            return false;
        }
        return true;
    }

    const validateEmail = () => {
        const emailRegex = /^(([^\s()<>[\]\\.,;:\s@\"]+(\.[^\s()<>[\]\\.,;:\s@\"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        if (!form.email || form.email.trim() === '') {
            setFormErrors(prevErrors => ({ ...prevErrors, email: 'Your email is required.' }));
            return false;
        } else if (!emailRegex.test(form.email)) {
            setFormErrors(prevErrors => ({ ...prevErrors, email: 'Please enter a valid email format.' }));
            return false;
        }
        return true;
    }

    const validatePhone = () => {
        const phoneDigits = form.phone.replace(/\D/g, '');
        if (!form.phone || form.phone.trim() === '') {
            setFormErrors(prevErrors => ({ ...prevErrors, phone: 'Your phone number is required.' }));
            return false;
        } else if (phoneDigits.length < 10) {
            setFormErrors(prevErrors => ({ ...prevErrors, phone: 'Your phone number must be at least 10 digits.' }));
            return false;
        }
        return true;
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateName() || !validateEmail() || !validatePhone()) return;
        setIsSubmitting(true);
        setSubmitError('');
        try {
            const response = await fetch(`${process.env.REACT_APP_TB2_API_URL}/affiliates`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: form.name, email: form.email, phone: form.phone, utmSource: source }),
            });
            const result = await response.json();
            if (!response.ok || !result.success) throw new Error(result.error || 'Submission failed.');
            trackPostHogEvent('affiliate_signup_submitted', { source });
            setSubmitted(true);
        } catch (err) {
            setSubmitError('Something went wrong. Please try again later.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <section className={styles.section} id="affiliate-signup">
            <div className={styles.inner}>
                {submitted ? (
                    <div className={styles.success}>
                        <div className={styles.checkCircle}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                        </div>
                        <h3 className={styles.successH3}>You're in — we'll be in touch shortly.</h3>
                        <p className={styles.successP}>Check your inbox for next steps.</p>
                    </div>
                ) : (
                    <>
                        <h2 className={styles.h2}>Ready to start?</h2>
                        <p className={styles.sub}>Sign up in under 5 minutes. Get your link the same day.</p>
                        <form className={styles.form} onSubmit={handleSubmit} noValidate>
                            <div className={styles.inputContainer}>
                                <input
                                    className={`${styles.input}${formErrors.name ? ' ' + styles.inputError : ''}`}
                                    name="name"
                                    value={form.name}
                                    placeholder="Full Name"
                                    onChange={handleInputChange}
                                    onFocus={handleClearError}
                                    onBlur={validateName}
                                />
                                {formErrors.name && <span className={styles.errorMessage}>{formErrors.name}</span>}
                            </div>
                            <div className={styles.inputContainer}>
                                <input
                                    className={`${styles.input}${formErrors.email ? ' ' + styles.inputError : ''}`}
                                    name="email"
                                    value={form.email}
                                    placeholder="Email address"
                                    type="email"
                                    onChange={handleInputChange}
                                    onFocus={handleClearError}
                                    onBlur={validateEmail}
                                />
                                {formErrors.email && <span className={styles.errorMessage}>{formErrors.email}</span>}
                            </div>
                            <div className={styles.inputContainer}>
                                <input
                                    className={`${styles.input}${formErrors.phone ? ' ' + styles.inputError : ''}`}
                                    name="phone"
                                    value={phoneDisplayValue}
                                    onChange={handlePhoneChange}
                                    onKeyDown={handlePhoneKeyDown}
                                    maxLength={14}
                                    placeholder="Phone number"
                                    type="tel"
                                    onFocus={handleClearError}
                                    onBlur={validatePhone}
                                />
                                {formErrors.phone && <span className={styles.errorMessage}>{formErrors.phone}</span>}
                            </div>
                            {isSubmitting ? (
                                <div className={styles.loaderWrap}>
                                    <CircularProgress sx={{ color: '#ffffff' }} size={28} />
                                </div>
                            ) : (
                                <button type="submit" className={styles.submitBtn}>
                                    Become an affiliate →
                                </button>
                            )}
                            {submitError && <p className={styles.submitError}>{submitError}</p>}
                        </form>
                        <p className={styles.note}>No cost to join · Your info is kept private</p>
                    </>
                )}
            </div>
        </section>
    );
}

export default AffiliateSignup;
